import { useCallback, useState } from 'react';
import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import { useDeactivateTeamsModal } from 'TeamModule/components/DeactivateTeamsModal/useDeactivateTeamsModal';
import { useScheduleDeactivateTeamsModal } from 'TeamModule/components/ScheduleDeactivateTeamsModal/useScheduleDeactivateTeamsModal';
import useTeams from 'hooks/useTeams';
import { useEditCsmModal } from './useEditCsmModal';
import { useEditTagsModal } from './useEditTagsModal';
import { useEditNicknamesModal } from './useEditNicknamesModal';
import { ActionMenu, ActionMenuSection, useActionMenu } from 'components/ActionMenu';
import useNavigationContext from 'hooks/useNavigationContext';
import useSandboxes from 'SandboxModule/hooks/useSandboxes';
import { Tenant } from 'TenantModule/types';
import useExportToExcel from 'hooks/useExportToExcel';
import { useMembersForExportMutation } from 'TeamModule/query/hooks/useMembersForExportMutation';
import {
  MemberSearchItem,
  SearchMembersByFieldsPayload,
} from 'TeamModule/api/searchMembersByFieldsApi';
import { GetMembersForExportResponse } from 'TeamModule/services/GetMembersForExportApi';
import { useGenerateTeamApiTokenModal } from 'TeamModule/hooks/useGenerateTeamApiTokenModal';
import useAppNavigation from 'hooks/useAppNavigation';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import SimpleModal from 'components/SimpleModal';
import { scheduleArchiveTeam } from 'ProfileModule/services/scheduleArchiveTeamApi';
import ApiClientManager from 'core/apiClient';
import { deleteBatchTimeEntries } from 'thunk';
import useExportDeletedWorkPlanCsvModal from 'TeamModule/components/ExportDeletedWorkPlanCsvModal/useExportDeletedWorkPlanCsvModal';
import useReactivateTeamsConfirmModal from 'hooks/useReactivateTeamsConfirmModal';
import { useSetRetentionPeriodModal } from './useSetRetentionPeriodModal';
import { useEnableDdiModal } from './useEnableDdiModal';
import { getCopyText } from 'utils';
import {
  buildAiBlockMenuItem,
  useSetTeamAiBlockedModal,
} from 'TeamModule/hooks/useSetTeamAiBlockedModal';
import { useAiSuggestionsAccess } from 'TeamModule/hooks/useAiSuggestionsAccess';
import {
  buildAiEnabledMenuItem,
  useSetTeamAiEnabledModal,
} from 'TeamModule/hooks/useSetTeamAiEnabledModal';
import { queryClient } from 'core/query/queryClient';
import { teamQueryKeys } from 'TeamModule/query/teamQueryKeys';

export const useTeamActionMenu = () => {
  const { openModal } = useDynamicModal();
  const { anchorEl, anchorPosition, isOpen, openMenu, closeMenu } = useActionMenu();
  const [currentTeamId, setCurrentTeamId] = useState<number | null>(null);
  const [currentArchivedAt, setCurrentArchivedAt] = useState<string | null>(null);
  const [currentScheduledArchiveDate, setCurrentScheduledArchiveDate] = useState<string | null>(
    null,
  );
  const [currentDdiEnabled, setCurrentDdiEnabled] = useState<boolean>(false);
  const [currentAiEnabled, setCurrentAiEnabled] = useState<boolean | undefined>(undefined);
  const [currentAiBlocked, setCurrentAiBlocked] = useState<boolean | undefined>(undefined);
  const [currentTeamName, setCurrentTeamName] = useState<string>('');
  // An AI Suggestions change (On / Off or Block / Unblock) is still being
  // applied or the page is still re-reading the team: a second one would race it.
  const [isAiChangePending, setIsAiChangePending] = useState(false);
  const { openSetTeamAiBlockedModal } = useSetTeamAiBlockedModal();
  const { openSetTeamAiEnabledModal } = useSetTeamAiEnabledModal();
  const { canToggle: canToggleAiSuggestions, canBlock: canBlockAiSuggestions } =
    useAiSuggestionsAccess();
  const { openDeactivateTeamsModal } = useDeactivateTeamsModal();
  const { openScheduleDeactivateTeamsModal } = useScheduleDeactivateTeamsModal();
  const { teamHash } = useTeams({ shouldAutoLoad: false });
  // Use navigation context (URL params > global state) as source of truth for tenant/realm
  const navigationContext = useNavigationContext();
  const { getSandbox } = useSandboxes();

  // Get realm object from sandbox cache for getCopyText display formatting
  const realm = navigationContext.realmId ? getSandbox(navigationContext.realmId) : undefined;
  const { openEditCsmModal } = useEditCsmModal();
  const { openEditTagsModal } = useEditTagsModal();
  const { openEditNicknamesModal } = useEditNicknamesModal();
  const { exportToExcel } = useExportToExcel();
  const { openGenerateApiTokenModal } = useGenerateTeamApiTokenModal();
  const { navigateToPricingCalculator } = useAppNavigation();
  const dispatch = useDispatch();
  const authToken = useSelector(selectAuthToken) as string | undefined;
  const { openExportDeletedWorkPlanCsvModal } = useExportDeletedWorkPlanCsvModal();
  const { handleOpenReactivateTeamsModal } = useReactivateTeamsConfirmModal();
  const { openSetRetentionPeriodModal } = useSetRetentionPeriodModal();
  const { openEnableDdiModal, isEnablingDdi } = useEnableDdiModal();

  const { exportMembers } = useMembersForExportMutation();

  const openTeamActionMenu = useCallback(
    ({
      target,
      teamId,
      archivedAt,
      scheduledArchiveDate,
      ddiEnabled,
      aiEnabled,
      aiBlocked,
      teamName,
    }: {
      target: HTMLElement;
      teamId: number;
      archivedAt?: string | null;
      scheduledArchiveDate?: string | null;
      ddiEnabled?: boolean;
      aiEnabled?: boolean;
      aiBlocked?: boolean;
      teamName?: string;
    }) => {
      setCurrentTeamId(teamId);
      setCurrentArchivedAt(archivedAt ?? null);
      setCurrentScheduledArchiveDate(scheduledArchiveDate ?? null);
      setCurrentDdiEnabled(Boolean(ddiEnabled));
      setCurrentAiEnabled(aiEnabled);
      setCurrentAiBlocked(aiBlocked);
      setCurrentTeamName(teamName ?? '');
      openMenu(target);
    },
    [openMenu],
  );

  const team = currentTeamId ? teamHash[currentTeamId] : null;
  // Archive/schedule state comes from the search API via the caller — the Redux
  // `teamHash` (Rails `fetchTeams`) exposes `deactivated_at` but not
  // `scheduledArchiveDate`, so we fall back to `deactivated_at` only for
  // `isArchived`. Callers that want the scheduled-deactivation UI to work must
  // pass `scheduledArchiveDate`.
  const isArchived = Boolean(currentArchivedAt || team?.deactivated_at);
  const isScheduled = Boolean(currentScheduledArchiveDate) && !isArchived;

  const headerOverrides =
    navigationContext.tenant && navigationContext.realmId
      ? ApiClientManager.buildUrlHeaders({
          tenantName: navigationContext.tenant,
          realmId: navigationContext.realmId,
        })
      : undefined;

  const textToCopy = team
    ? getCopyText({
        realm,
        tenant: navigationContext.tenant
          ? ({ tenantName: navigationContext.tenant } as Tenant)
          : undefined,
        team,
      })
    : '';

  const sections: ActionMenuSection[] = currentTeamId
    ? [
        {
          title: 'Quick Actions',
          items: [
            {
              label: 'Copy Team Info',
              onClick: () => {
                if (textToCopy) {
                  void navigator.clipboard.writeText(textToCopy);
                }
              },
              disabled: !textToCopy,
            },
            {
              label: 'Export to Excel',
              onClick: () => {
                type ExportPayload = SearchMembersByFieldsPayload & { all?: boolean };
                const q: ExportPayload = {
                  teamId: String(currentTeamId),
                  page: 1,
                  limit: 50,
                  sortBy: 'lastBackfilled',
                  sortOrder: 'DESC',
                  all: true,
                };
                exportMembers(q as unknown as SearchMembersByFieldsPayload, {
                  onSuccess: (result) => {
                    const response = result?.data as GetMembersForExportResponse | undefined;
                    const rows: MemberSearchItem[] = (response?.data as MemberSearchItem[]) || [];
                    const fileName = `team-${currentTeamId}-members`;
                    exportToExcel({ data: rows, fileName });
                  },
                });
              },
            },
            {
              label: 'Deleted Work Plan Export',
              onClick: () => openExportDeletedWorkPlanCsvModal({ teamId: currentTeamId }),
            },
          ],
        },
        {
          title: 'Team Configuration',
          items: [
            { label: 'Edit CSM', onClick: () => openEditCsmModal({ teamId: currentTeamId }) },
            { label: 'Edit Tags', onClick: () => openEditTagsModal({ teamId: currentTeamId }) },
            {
              label: 'Edit Nicknames',
              onClick: () => openEditNicknamesModal({ teamId: currentTeamId }),
            },
            {
              label: 'Add API Token',
              onClick: () => openGenerateApiTokenModal({ teamId: currentTeamId }),
            },
            {
              label: 'Enable DDI',
              onClick: () => openEnableDdiModal({ teamId: currentTeamId }),
              disabled: currentDdiEnabled || isEnablingDdi,
            },
            buildAiEnabledMenuItem({
              aiEnabled: currentAiEnabled,
              aiBlocked: currentAiBlocked,
              enabled: canToggleAiSuggestions && !isAiChangePending,
              onSelect: (aiEnabled) =>
                openSetTeamAiEnabledModal({
                  tenant: navigationContext.tenant || realm?.tenant || '',
                  realmId: navigationContext.realmId || '',
                  teamId: currentTeamId,
                  teamName: currentTeamName || team?.name || String(currentTeamId),
                  aiEnabled,
                  onPendingChange: setIsAiChangePending,
                  // The team page reads the team from a Teams list query.
                  onSuccess: () =>
                    queryClient.invalidateQueries({
                      queryKey: teamQueryKeys.lists(),
                      refetchType: 'all',
                    }),
                }),
            }),
            buildAiBlockMenuItem({
              aiBlocked: currentAiBlocked,
              enabled: canBlockAiSuggestions && !isAiChangePending,
              onSelect: (aiBlocked) =>
                openSetTeamAiBlockedModal({
                  tenant: navigationContext.tenant || realm?.tenant || '',
                  realmId: navigationContext.realmId || '',
                  teamId: currentTeamId,
                  teamName: currentTeamName || team?.name || String(currentTeamId),
                  aiBlocked,
                  onPendingChange: setIsAiChangePending,
                }),
            }),
            {
              label: 'Calculate Renewal',
              onClick: () => navigateToPricingCalculator({ team_id: currentTeamId }),
            },
          ],
        },
        {
          title: 'Team Lifecycle',
          items: [
            {
              label: 'Activation',
              subItems: [
                {
                  label: 'Deactivate Team',
                  onClick: () =>
                    openDeactivateTeamsModal({
                      teamTargets: [
                        {
                          teamId: currentTeamId,
                          tenant: navigationContext.tenant || realm?.tenant || '',
                          realmId: navigationContext.realmId || '',
                        },
                      ],
                      teamNamesById: team
                        ? { [currentTeamId]: team.name || '' }
                        : { [currentTeamId]: String(currentTeamId) },
                    }),
                  disabled: isArchived,
                },
                {
                  label: 'Schedule Deactivation',
                  onClick: () =>
                    openScheduleDeactivateTeamsModal({
                      teamTargets: [
                        {
                          teamId: currentTeamId,
                          tenant: navigationContext.tenant || realm?.tenant || '',
                          realmId: navigationContext.realmId || '',
                        },
                      ],
                      teamNamesById: team
                        ? { [currentTeamId]: team.name || '' }
                        : { [currentTeamId]: String(currentTeamId) },
                    }),
                  disabled: isArchived || isScheduled,
                },
                {
                  label: 'Reactivate Team',
                  onClick: () =>
                    handleOpenReactivateTeamsModal({
                      teamIds: [currentTeamId],
                    }),
                  disabled: !isArchived,
                },
                {
                  label: 'Set Data Retention Period',
                  onClick: () => openSetRetentionPeriodModal({ teamId: currentTeamId }),
                  disabled: !(isArchived || isScheduled),
                },
                {
                  label: 'Cancel Scheduled Deactivation',
                  onClick: () =>
                    openModal({
                      modal: ({ toggle: closer }) => (
                        <SimpleModal
                          open
                          onClose={closer}
                          onConfirm={async () => {
                            try {
                              await scheduleArchiveTeam({
                                params: {
                                  teamId: currentTeamId,
                                  scheduledArchiveDate: '',
                                  reasonToArchive: 'cancel scheduled archive',
                                } as any,
                                token: authToken as string,
                                config: headerOverrides ? { headers: headerOverrides } : {},
                              });
                            } catch (err) {
                              console.error('Failed to cancel scheduled deactivation', err);
                            }
                            closer();
                          }}
                          closeOnConfirm
                          headerText="Cancel Scheduled Deactivation"
                          Body={
                            <div>Cancel the scheduled deactivation for team {currentTeamId}?</div>
                          }
                        />
                      ),
                    }),
                  disabled: isArchived || !isScheduled,
                },
              ],
            },
          ],
        },
        {
          title: 'Danger Zone',
          items: [
            {
              label: 'Wipeout',
              subItems: [
                { label: 'Wipe out Members', disabled: true },
                { label: 'Wipe out Projects', disabled: true },
                { label: 'Wipe out Work Plan', disabled: true },
                {
                  label: 'Wipe out Timesheet',
                  danger: true,
                  onClick: () =>
                    openModal({
                      modal: ({ toggle: closer }) => (
                        <SimpleModal
                          open
                          onClose={closer}
                          onConfirm={() => {
                            // TODO: Fix with Redux Toolkit configureStore migration
                            dispatch(deleteBatchTimeEntries({ team_id: currentTeamId }) as any);
                            closer();
                          }}
                          closeOnConfirm
                          headerText="Wipe out Timesheet"
                          Body={
                            <div>
                              This will permanently delete all timesheet entries for team{' '}
                              {currentTeamId}. Continue?
                            </div>
                          }
                        />
                      ),
                    }),
                },
              ],
            },
          ],
        },
      ]
    : [];

  return {
    openTeamActionMenu,
    TeamActionMenuComponent: (
      <ActionMenu
        anchorEl={anchorEl}
        anchorPosition={anchorPosition}
        open={isOpen}
        onClose={closeMenu}
        sections={sections}
        title="Team Actions"
      />
    ),
  };
};

export default useTeamActionMenu;
